// CompressedCalendarView.js - Reduced vertical space with "Full" indicator
import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { 
  Typography, 
  Box, 
  Grid, 
  Button, 
  CircularProgress, 
  useTheme,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BoltIcon from '@mui/icons-material/Bolt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GroupIcon from '@mui/icons-material/Group';
import { getVenuePrice } from 'api';

// Custom props that should not be forwarded to DOM
const customProps = ['isToday', 'isSelected', 'hasAvailability', 'isPast', 'isDiscounted', 'isBestPrice', 'isCurrentMonth'];
const shouldForwardProp = (prop) => !customProps.includes(prop);

// Styled components
const CompressedContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  width: '100%'
}));

const MonthHeader = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  textAlign: 'center',
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(0.5)
}));

const WeekdaysHeader = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(0.5)
}));

const WeekdayCell = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(0.5, 0),
  fontWeight: 500,
  color: theme.palette.text.secondary,
  fontSize: '0.8rem'
}));

const DaysGrid = styled(Grid)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`
}));

// Much more compact day cell
const DayCell = styled(Box, { shouldForwardProp })(({
  theme, 
  isToday, 
  isSelected, 
  hasAvailability, 
  isPast 
}) => ({
  height: '50px', // Significantly reduced height
  padding: theme.spacing(0.5),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: hasAvailability && !isPast ? 'pointer' : 'default',
  backgroundColor: isSelected 
    ? theme.palette.warning.light + '20'  // Light orange background
    : isToday 
      ? theme.palette.info.light + '15'  // Light blue for today
      : 'transparent',
  borderRight: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  opacity: isPast ? 0.7 : 1,
  boxShadow: isSelected ? `inset 0 0 0 2px ${theme.palette.warning.main}` : 'none',
  '&:hover': hasAvailability && !isPast ? {
    backgroundColor: isSelected 
      ? theme.palette.warning.light + '30'
      : theme.palette.action.hover
  } : {},
  '&:last-child': {
    borderRight: 'none',
  }
}));

const DayNumber = styled(Typography, { shouldForwardProp })(({ theme, isToday, isSelected }) => ({
  fontSize: '1rem',
  lineHeight: 1,
  color: isSelected 
    ? theme.palette.warning.main  // Orange for selected
    : isToday 
      ? theme.palette.primary.main  // Primary color for today
      : theme.palette.text.primary,
  fontWeight: isToday || isSelected ? 600 : 500,
  marginTop: theme.spacing(0.25),
  marginBottom: theme.spacing(0.25)
}));

const OriginalPrice = styled(Typography)(({ theme }) => ({
  fontSize: '0.65rem',
  lineHeight: 1,
  textDecoration: 'line-through',
  color: theme.palette.text.disabled,
  marginBottom: theme.spacing(-0.25)
}));

const PriceTag = styled(Typography, { shouldForwardProp })(({ 
  theme, 
  isDiscounted, 
  isBestPrice 
}) => ({
  fontSize: '0.9rem',
  lineHeight: 1,
  fontWeight: 600,
  color: isDiscounted 
    ? theme.palette.success.main  // Green for discounted
    : isBestPrice 
      ? theme.palette.warning.main  // Orange for best price
      : theme.palette.text.primary,
  marginTop: 'auto'
}));

const UnavailableText = styled(Typography)(({ theme }) => ({
  fontSize: '0.65rem',
  lineHeight: 1,
  color: theme.palette.text.disabled,
  marginTop: 'auto'
}));

const InstantIcon = styled(BoltIcon)(({ theme }) => ({
  position: 'absolute',
  top: 2,
  right: 2,
  fontSize: '0.8rem',
  color: theme.palette.warning.main
}));

const ShowMoreButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  padding: theme.spacing(1),
  fontSize: '0.9rem'
}));

const LegendContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2.5),
  flexWrap: 'wrap',
  padding: theme.spacing(0.5)
}));

const LegendItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5)
}));

const ColorDot = styled(Box)(({ theme, color }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: color
}));

const LegendText = styled(Typography)(({ theme }) => ({
  fontSize: '0.85rem',
  color: theme.palette.text.secondary
}));

// Community activity indicator (yellow dot)
const CommunityIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 2,
  left: 2,
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.warning.main,
  cursor: 'pointer',
  zIndex: 2,
  '&:hover': {
    transform: 'scale(1.3)',
  }
}));

const CompressedCalendarView = ({
  slug,
  onDateSelect,
  defaultDate,
  selectedPrice,
  onShowFullCalendar,
  priceDataLoading = false,
  workingDays = [1, 2, 3, 4, 5], // Mon-Fri by default
  fixedSlotPrice = null, // New prop for meeting room fixed price
  isMeetingRoom = false, // New prop to determine if this is a meeting room
  venue = null, // New prop for venue data
  communityBookingsByDate = {} // Community bookings for calendar indicators
}) => {
  const theme = useTheme();
  const [prices, setPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    defaultDate ? defaultDate.format('YYYY-MM-DD') : null
  );
  
  // Use useMemo to calculate date ranges so they don't change on every render
  const dateRanges = useMemo(() => {
    const today = dayjs();
    const startOfCurrentWeek = today.startOf('week');
    const endOfNextWeek = startOfCurrentWeek.clone().add(13, 'day');
    
    return {
      today,
      currentMonth: today.format('MMMM YYYY'),
      startOfCurrentWeek,
      endOfNextWeek,
      startDate: startOfCurrentWeek.format('YYYY-MM-DD'),
      endDate: endOfNextWeek.format('YYYY-MM-DD')
    };
  }, []); // Empty dependency array means this only calculates once
  
  // Effect to synchronize selectedDate with defaultDate
  useEffect(() => {
    if (defaultDate) {
      setSelectedDate(defaultDate.format('YYYY-MM-DD'));
    }
  }, [defaultDate]);
  
  // Effect to synchronize selectedDate with selectedPrice
  useEffect(() => {
    if (selectedPrice && selectedPrice.start) {
      setSelectedDate(dayjs(selectedPrice.start).format('YYYY-MM-DD'));
    }
  }, [selectedPrice]);
  
  // Effect to fetch prices for the displayed weeks - only if not a meeting room
  useEffect(() => {
    // Skip price fetching for meeting rooms with fixed price
    if (isMeetingRoom && fixedSlotPrice !== null) return;
    
    let isMounted = true; // For cleanup
    
    const fetchPrices = async () => {
      if (!slug) return; // Don't fetch if no slug
      
      setLoadingPrices(true);
      try {
        const params = {
          start: dateRanges.startDate,
          end: dateRanges.endDate
        };
        
        const response = await getVenuePrice(slug, params);
        
        // Only update state if component is still mounted
        if (isMounted && response && response.prices) {
          setPrices(response.prices);
        }
      } catch (error) {
        console.error('Error fetching prices:', error);
      } finally {
        if (isMounted) {
          setLoadingPrices(false);
        }
      }
    };
    
    fetchPrices();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [slug, dateRanges.startDate, dateRanges.endDate, isMeetingRoom, fixedSlotPrice]);
  
  // Generate fixed price data for meeting rooms
  useEffect(() => {
    if (isMeetingRoom && fixedSlotPrice !== null) {
      // Create "pseudo" price objects for all working days in the range
      const generatedPrices = [];
      let currentDay = dateRanges.startOfCurrentWeek.clone();
      const endDay = dateRanges.endOfNextWeek.clone();
      
      while (currentDay.isBefore(endDay) || currentDay.isSame(endDay, 'day')) {
        if (workingDays.includes(currentDay.day() || 7)) {
          // Create a price object for each working day
          generatedPrices.push({
            start: currentDay.format('YYYY-MM-DD'),
            price: `${fixedSlotPrice.toString()}/hr`,
            available: true, // All days are considered available
            instant_booking: true // You can set this based on your business logic
          });
        }
        currentDay = currentDay.add(1, 'day');
      }
      
      setPrices(generatedPrices);
      setLoadingPrices(false);
    }
  }, [dateRanges.startOfCurrentWeek, dateRanges.endOfNextWeek, workingDays, isMeetingRoom, fixedSlotPrice]);
  
  // Find the lowest price to determine the "best price"
  const lowestPrice = useMemo(() => {
    // For meeting rooms with fixed price, all prices are the same
    if (isMeetingRoom && fixedSlotPrice !== null) return fixedSlotPrice;
    
    const validPrices = prices.filter(p => parseFloat(p.price) > 0);
    if (validPrices.length === 0) return 0;
    return Math.min(...validPrices.map(p => parseFloat(p.price)));
  }, [prices, isMeetingRoom, fixedSlotPrice]);
  
  // Helper to check if a date is today
  const isToday = (date) => date.format('YYYY-MM-DD') === dateRanges.today.format('YYYY-MM-DD');
  
  // Helper to check if a date is in the past
  const isPastDate = (date) => date.isBefore(dateRanges.today, 'day');
  
  // Helper to get the price object for a given date
  const getPriceObjectForDate = (dateStr) => {
    // For meeting rooms, if we have a fixed price, create a price object
    if (isMeetingRoom && fixedSlotPrice !== null) {
      const priceObject = prices.find(p => p.start && p.start === dateStr);
      
      // If found in our generated prices, return it
      if (priceObject) return priceObject;
      
      // Otherwise create a default one if it's a working day
      const date = dayjs(dateStr);
      if (workingDays.includes(date.day() || 7) && !isPastDate(date)) {
        return {
          start: dateStr,
          price: `${fixedSlotPrice.toString()}/hr`,
          available: true,
          instant_booking: true
        };
      }
      return null;
    }
    
    // First check if the selectedPrice prop matches this date
    if (selectedPrice && selectedPrice.start && dayjs(selectedPrice.start).format('YYYY-MM-DD') === dateStr) {
      return selectedPrice;
    }
    
    // Otherwise use the price from the internal prices array
    return prices.find(p => p.start && dayjs(p.start).format('YYYY-MM-DD') === dateStr);
  };
  
  // Helper to check if a date has available booking
  const isDateAvailable = (date, priceObject) => {
    // Past dates are never available
    if (isPastDate(date)) return false;
    
    // For meeting rooms, all non-past working days are available
    // unless they're explicitly marked as unavailable
    if (isMeetingRoom && fixedSlotPrice !== null) {
      // Check if there's a specific price object with availability set to false
      if (priceObject && priceObject.available === false) {
        return false;
      }
      return workingDays.includes(date.day() || 7);
    }
    
    // For hot desks:
    
    // 1. If there's no price object or it's marked as unavailable, return false
    if (!priceObject || priceObject.available === false || priceObject.available === '-') {
      return false;
    }
    
    // 2. If available is a number, check if it's greater than 0
    if (typeof priceObject.available === 'number') {
      return priceObject.available > 0;
    }
    
    // 3. If available is a string that can be parsed as a number
    if (priceObject.available && !isNaN(parseInt(priceObject.available))) {
      return parseInt(priceObject.available) > 0;
    }
    
    // 4. Check if price is valid (greater than 0)
    return parseFloat(priceObject.price) > 0;
  };
  
  // Helper to determine if a price is discounted
  const isDiscountedPrice = (priceObject) => {
    // Meeting rooms don't have discounted pricing
    if (isMeetingRoom && fixedSlotPrice !== null) return false;
    
    if (!priceObject) return false;
    
    const price = parseFloat(priceObject.price);
    const originalPrice = parseFloat(priceObject.original_price || 0);
    
    return originalPrice > 0 && price < originalPrice && price !== lowestPrice;
  };
  
  // Helper to determine if a price is the best price
  const isBestPrice = (priceObject) => {
    // Meeting rooms don't have "best price" highlights
    if (isMeetingRoom && fixedSlotPrice !== null) return false;
    
    if (!priceObject) return false;
    
    const price = parseFloat(priceObject.price);
    return price === lowestPrice && lowestPrice > 0;
  };
  
  // Handler for date selection
  const handleDateSelect = (date, priceObject) => {
    if (isDateAvailable(date, priceObject)) {
      setSelectedDate(date.format('YYYY-MM-DD'));
      
      // For meeting rooms, create a consistent price object
      if (isMeetingRoom && fixedSlotPrice !== null && (!priceObject || !priceObject.start)) {
        const newPriceObject = {
          start: date.format('YYYY-MM-DD'),
          price: `${fixedSlotPrice.toString()}/hr`,
          available: true,
          instant_booking: true,
          // Any other properties required by your application
        };
        onDateSelect(newPriceObject);
      } else {
        onDateSelect(priceObject);
      }
    }
  };
  
  // Organize days into a grid with weeks
  const organizeDaysIntoWeeks = useMemo(() => {
    let allDays = [];
    let currentDay = dateRanges.startOfCurrentWeek.clone();
    const endDay = dateRanges.endOfNextWeek.clone();
    
    // Get all working days in the 2-week period
    while (currentDay.isBefore(endDay) || currentDay.isSame(endDay, 'day')) {
      if (workingDays.includes(currentDay.day() || 7)) {
        allDays.push(currentDay.clone());
      }
      currentDay = currentDay.add(1, 'day');
    }
    
    // Split into rows of 5 days (or however many working days there are per week)
    const rows = [];
    const daysPerRow = Math.min(workingDays.length, 7); // Maximum 7 days per row
    
    for (let i = 0; i < allDays.length; i += daysPerRow) {
      rows.push(allDays.slice(i, i + daysPerRow));
    }
    
    return rows;
  }, [dateRanges.startOfCurrentWeek, dateRanges.endOfNextWeek, workingDays]);
  
  // Generate grid with days
  const daysGrid = useMemo(() => {
    // Get the weekday names
    const weekdaysMap = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    // Filter to only include working days
    const weekdays = weekdaysMap.filter((_, index) => workingDays.includes(index + 1));
    
    return (
      <>
        {/* Month header */}
        <MonthHeader>
          {dateRanges.currentMonth}
        </MonthHeader>
        
        {/* Weekdays header */}
        <WeekdaysHeader container>
          {weekdays.map(day => (
            <Grid item xs={12 / weekdays.length} key={day}>
              <WeekdayCell>{day}</WeekdayCell>
            </Grid>
          ))}
        </WeekdaysHeader>
        
        {/* Days grid - render each week as a row */}
        {organizeDaysIntoWeeks.map((week, weekIndex) => (
          <DaysGrid container key={`week-${weekIndex}`}>
            {week.map(day => {
              const dateStr = day.format('YYYY-MM-DD');
              const priceObject = getPriceObjectForDate(dateStr);
              const isDateSelected = selectedDate === dateStr;
              const available = isDateAvailable(day, priceObject);
              const past = isPastDate(day);
              const discounted = isDiscountedPrice(priceObject);
              const bestPrice = isBestPrice(priceObject);
              const communityActivity = communityBookingsByDate[dateStr];
              const hasCommunityActivity = communityActivity && communityActivity.count > 0;
              
              return (
                <Grid item xs={12 / week.length} key={dateStr}>
                  <DayCell
                    isToday={isToday(day)}
                    isSelected={isDateSelected}
                    hasAvailability={available}
                    isPast={past}
                    onClick={() => handleDateSelect(day, priceObject)}
                  >
                    {/* Community activity indicator */}
                    {hasCommunityActivity && (
                      <Tooltip 
                        title={`${communityActivity.count} community member${communityActivity.count > 1 ? 's' : ''} booking`}
                        arrow
                      >
                        <CommunityIndicator />
                      </Tooltip>
                    )}
                    
                    {/* Only show instant booking icon for available slots with instant booking */}
                    {available && (!isMeetingRoom || (priceObject && priceObject.instant_booking)) && <InstantIcon />}
                    
                    <DayNumber isToday={isToday(day)} isSelected={isDateSelected}>
                      {day.format('D')}
                    </DayNumber>
                    
                    {/* Cell content based on availability */}
                    {available ? (
                      // Display price for available slots
                      isMeetingRoom && fixedSlotPrice !== null ? (
                        // For meeting rooms with fixed price
                        <PriceTag>
                          £{`${fixedSlotPrice.toString()}/hr`}
                        </PriceTag>
                      ) : (
                        // For hot desks with dynamic pricing
                        priceObject && parseFloat(priceObject.price) > 0 ? (
                          <>
                            {priceObject.original_price && parseFloat(priceObject.original_price) > parseFloat(priceObject.price) && (
                              <OriginalPrice>
                                £{parseFloat(priceObject.original_price).toFixed(0)}
                              </OriginalPrice>
                            )}
                            
                            <PriceTag 
                              isDiscounted={discounted}
                              isBestPrice={bestPrice}
                            >
                              £{parseFloat(priceObject.price).toFixed(0)}
                            </PriceTag>
                          </>
                        ) : null
                      )
                    ) : (
                      // Display "Full" or "Past" for unavailable slots
                      <UnavailableText>
                        {past ? 'Past' : 'Full'}
                      </UnavailableText>
                    )}
                  </DayCell>
                </Grid>
              );
            })}
          </DaysGrid>
        ))}
      </>
    );
  }, [
    dateRanges.currentMonth,
    organizeDaysIntoWeeks,
    workingDays, 
    prices, 
    selectedDate, 
    lowestPrice,
    isMeetingRoom,
    fixedSlotPrice,
    communityBookingsByDate
  ]);
  
  return (
    <CompressedContainer>
      {loadingPrices || priceDataLoading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '120px' // Reduced loading height
        }}>
          <CircularProgress size={20} />
          <Typography variant="body2" sx={{ ml: 1, fontSize: '0.9rem' }}>
            Loading calendar...
          </Typography>
        </Box>
      ) : (
        <>
          {/* Display the days grid */}
          {daysGrid}
          
          {/* Show Full Calendar button or redirect to website */}
          {venue?.status === 'PA' && !venue?.website ? (
            <Box sx={{ py: 2, textAlign: 'center', bgcolor: theme.palette.warning.light, borderRadius: 1, p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                This venue is pending approval. Please contact us for more information.
              </Typography>
            </Box>
          ) : (
            <ShowMoreButton 
              variant="text" 
              color="primary"
              startIcon={<CalendarTodayIcon sx={{ fontSize: '1rem' }} />}
              size="small"
              onClick={() => {
                if (venue?.status === 'PA' && venue?.website) {
                  // If venue is pre-approved with website, open it
                  window.open(venue.website, '_blank');
                } else {
                  // Otherwise show full calendar
                  onShowFullCalendar();
                }
              }}
            >
              {venue?.status === 'PA' && venue?.website ? 'VISIT WEBSITE' : 'SHOW FULL CALENDAR'}
            </ShowMoreButton>
          )}
          
          {/* Legend - only show for hot desks, not for meeting rooms */}
          {!isMeetingRoom && (
            <LegendContainer>
              <LegendItem>
                <ColorDot color={theme.palette.success.main} />
                <LegendText>
                  Discounted price
                </LegendText>
              </LegendItem>
              
              <LegendItem>
                <ColorDot color={theme.palette.warning.main} />
                <LegendText>
                  Best price
                </LegendText>
              </LegendItem>
              
              <LegendItem>
                <BoltIcon sx={{ color: theme.palette.warning.main, fontSize: '0.8rem' }} />
                <LegendText>
                  Instant booking
                </LegendText>
              </LegendItem>
            </LegendContainer>
          )}
          
          {/* For meeting rooms, show simplified legend if needed */}
          {isMeetingRoom && (
            <LegendContainer>
              <LegendItem>
                <BoltIcon sx={{ color: theme.palette.warning.main, fontSize: '0.8rem' }} />
                <LegendText>
                  Instant booking
                </LegendText>
              </LegendItem>
            </LegendContainer>
          )}
        </>
      )}
    </CompressedContainer>
  );
};

export default CompressedCalendarView;